import React from "react";
import { notification } from "antd";
import { updateNotification } from "../redux/notifications/notification.slice";

/**
 * Enum for Notification Severities
 */
export enum NotificationSeverity {
    INFO = "info",
    WARNING = "warning",
    ERROR = "error",
    SUCCESS = "success",
    OTHER = "other",
    LOADING = "loading",
}

/**
 * Enum for Notification Categories
 */
export enum NotificationCategory {
    MESSAGE = "message",
    AUDIT = "audit",
    REPORT = "report",
    ALERT = "alert",
    REMINDER = "reminder",
    UPDATE = "update",
    USER_JOIN = "user_join",
    EVALUATION = "evaluation",
}

/**
 * Enum for Notification Priorities
 */
export enum NotificationPriority {
    LOW = "low",
    MEDIUM = "medium",
    HIGH = "high",
}

/**
 * Interface for Minimal Toast Parameters
 */
interface MinimalToastParams {
    _id?: string; // Added to support marking notifications as read
    title: string;
    message: string;
    type?: NotificationSeverity;
    description?: string;
    onClick?: () => void;
    category: NotificationCategory;
    priority: NotificationPriority;
    link?: string;
    data?: any;
    expiresAt?: Date;
    /** Overrides default duration (seconds). LOADING always uses 0 unless > 0. */
    durationSeconds?: number;
}

// ---------------------------------------------------------------------------
// Editorial palette (mirrors styles/tokens.css from the design reference)
// ---------------------------------------------------------------------------
const FONT_SANS =
    `'Geist', 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
const FONT_MONO =
    `'Geist Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace`;

const SEV: Record<
    NotificationSeverity,
    { ink: string; bg: string; accent: string; label: string }
> = {
    [NotificationSeverity.SUCCESS]: {
        ink: "#145c3e",
        bg: "#e6f5ec",
        accent: "#2f9e6f",
        label: "Success",
    },
    [NotificationSeverity.INFO]: {
        ink: "#1f44b3",
        bg: "#eef4ff",
        accent: "#3a72ff",
        label: "Info",
    },
    [NotificationSeverity.WARNING]: {
        ink: "#6b4a0e",
        bg: "#fdf5e0",
        accent: "#d49019",
        label: "Warning",
    },
    [NotificationSeverity.ERROR]: {
        ink: "#7a1f1f",
        bg: "#fdecec",
        accent: "#d23b3b",
        label: "Error",
    },
    [NotificationSeverity.LOADING]: {
        ink: "#2a2620",
        bg: "#faf8f4",
        accent: "#2a2620",
        label: "Loading",
    },
    [NotificationSeverity.OTHER]: {
        ink: "#2a2620",
        bg: "#ffffff",
        accent: "#7a7468",
        label: "Note",
    },
};

// One-time stylesheet injection: keyframes + neutralize antd's default chrome
// so our card can render flush inside the notification slot.
const STYLE_ID = "p3m-editorial-toast-styles";
const ensureStyles = () => {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;
    const tag = document.createElement("style");
    tag.id = STYLE_ID;
    tag.textContent = `
@keyframes p3mToastSlide { from { transform: translateY(8px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes p3mToastProgress { from { width: 100%; } to { width: 0%; } }
@keyframes p3mToastSweep { 0% { left: -40%; } 100% { left: 100%; } }
@keyframes p3mToastSpin { to { transform: rotate(360deg); } }

.ant-notification-notice.p3m-toast,
.ant-notification-notice-wrapper .ant-notification-notice.p3m-toast {
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  width: 380px !important;
}
.ant-notification-notice.p3m-toast .ant-notification-notice-message,
.ant-notification-notice.p3m-toast .ant-notification-notice-description {
  margin: 0 !important;
  padding: 0 !important;
}
.ant-notification-notice.p3m-toast .ant-notification-notice-message:empty { display: none !important; }
.ant-notification-notice.p3m-toast .ant-notification-notice-icon,
.ant-notification-notice.p3m-toast .ant-notification-notice-btn,
.ant-notification-notice.p3m-toast .ant-notification-notice-progress,
.ant-notification-notice.p3m-toast .ant-notification-notice-close { display: none !important; }
.ant-notification-notice.p3m-toast .ant-notification-notice-with-icon { padding: 0 !important; }

.p3m-toast-card { font-family: ${FONT_SANS}; }
.p3m-toast-card button {
  font-family: inherit;
  cursor: pointer;
  transition: opacity .15s ease, background .15s ease, border-color .15s ease;
}
.p3m-toast-card button:hover { opacity: 0.88; }
.p3m-toast-card .p3m-toast-close:hover { background: #f4f1ec; color: #2a2620; }
.p3m-toast-card:hover .p3m-toast-progress-fill { animation-play-state: paused; }
`;
    document.head.appendChild(tag);
};

// ---------------------------------------------------------------------------
// Inline icons (kept local so the toast has zero asset dependencies)
// ---------------------------------------------------------------------------
const Icon = {
    check: (s = 13) => (
        <svg
            width={s}
            height={s}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polyline points="20 6 9 17 4 12" />
        </svg>
    ),
    info: (s = 13) => (
        <svg
            width={s}
            height={s}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),
    warn: (s = 13) => (
        <svg
            width={s}
            height={s}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
    ),
    err: (s = 13) => (
        <svg
            width={s}
            height={s}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
    ),
    x: (s = 12) => (
        <svg
            width={s}
            height={s}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    ),
    loader: (s = 13) => (
        <svg
            width={s}
            height={s}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            style={{
                animation: "p3mToastSpin 0.9s linear infinite",
                transformOrigin: "center",
            }}
        >
            <path d="M21 12a9 9 0 1 1-6.2-8.55" />
        </svg>
    ),
};

const iconForSeverity = (sev: NotificationSeverity) => {
    switch (sev) {
        case NotificationSeverity.SUCCESS:
            return Icon.check();
        case NotificationSeverity.ERROR:
            return Icon.err();
        case NotificationSeverity.WARNING:
            return Icon.warn();
        case NotificationSeverity.LOADING:
            return Icon.loader();
        case NotificationSeverity.INFO:
        default:
            return Icon.info();
    }
};

// ---------------------------------------------------------------------------
// Card - the visual unit rendered inside antd's notification slot
// ---------------------------------------------------------------------------
interface ToastCardProps {
    severity: NotificationSeverity;
    title: string;
    body?: string;
    link?: string;
    showMarkRead: boolean;
    onMarkRead?: () => void;
    onClose: () => void;
    duration: number; // seconds; 0 = persistent
    isLoading: boolean;
    priority: NotificationPriority;
}

const ToastCard: React.FC<ToastCardProps> = ({
    severity,
    title,
    body,
    link,
    showMarkRead,
    onMarkRead,
    onClose,
    duration,
    isLoading,
    priority,
}) => {
    const s = SEV[severity] || SEV[NotificationSeverity.INFO];
    const showActions = !!link || showMarkRead;
    const showProgress = !isLoading && duration > 0;

    return (
        <div
            className="p3m-toast-card"
            style={{
                width: 380,
                background: "#ffffff",
                border: "1px solid #e8e6e0",
                borderLeft: `3px solid ${s.accent}`,
                borderRadius: 8,
                boxShadow:
                    "0 8px 24px rgba(20,16,8,0.08), 0 1px 3px rgba(20,16,8,0.06)",
                overflow: "hidden",
                animation: "p3mToastSlide 220ms cubic-bezier(.22,1,.36,1)",
            }}
        >
            <div
                style={{
                    padding: "12px 14px",
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-start",
                }}
            >
                <div
                    style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        display: "grid",
                        placeItems: "center",
                        background: s.bg,
                        color: s.ink,
                        flexShrink: 0,
                    }}
                >
                    {iconForSeverity(severity)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 2,
                        }}
                    >
                        <span
                            style={{
                                fontFamily: FONT_MONO,
                                fontSize: 9.5,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.1em",
                                color: s.ink,
                            }}
                        >
                            {s.label}
                        </span>
                        {priority === NotificationPriority.HIGH && (
                            <span
                                style={{
                                    fontFamily: FONT_MONO,
                                    fontSize: 9,
                                    fontWeight: 600,
                                    letterSpacing: "0.08em",
                                    color: "#7a1f1f",
                                    background: "#fdecec",
                                    padding: "1px 5px",
                                    borderRadius: 3,
                                }}
                            >
                                HIGH
                            </span>
                        )}
                        <span
                            style={{
                                fontFamily: FONT_MONO,
                                fontSize: 9.5,
                                color: "#7a7468",
                            }}
                        >
                            · just now
                        </span>
                    </div>
                    <div
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#0a0d14",
                            lineHeight: 1.35,
                            letterSpacing: "-0.005em",
                            marginBottom: body ? 3 : 0,
                            wordBreak: "break-word",
                        }}
                    >
                        {title}
                    </div>
                    {body && (
                        <div
                            style={{
                                fontSize: 12,
                                color: "#564f43",
                                lineHeight: 1.45,
                                wordBreak: "break-word",
                            }}
                        >
                            {body}
                        </div>
                    )}
                    {showActions && (
                        <div
                            style={{
                                display: "flex",
                                gap: 6,
                                marginTop: 9,
                            }}
                        >
                            {link && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(link);
                                    }}
                                    disabled={isLoading}
                                    style={{
                                        height: 26,
                                        padding: "0 10px",
                                        borderRadius: 5,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        background: "#0a0d14",
                                        color: "#fff",
                                        border: "none",
                                        opacity: isLoading ? 0.5 : 1,
                                    }}
                                >
                                    View
                                </button>
                            )}
                            {showMarkRead && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkRead?.();
                                        onClose();
                                    }}
                                    disabled={isLoading}
                                    style={{
                                        height: 26,
                                        padding: "0 10px",
                                        borderRadius: 5,
                                        fontSize: 11.5,
                                        fontWeight: 600,
                                        background: "transparent",
                                        color: "#2a2620",
                                        border: "1px solid #d4d0c9",
                                        opacity: isLoading ? 0.5 : 1,
                                    }}
                                >
                                    Mark as read
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    aria-label="Dismiss"
                    className="p3m-toast-close"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    style={{
                        width: 22,
                        height: 22,
                        borderRadius: 4,
                        color: "#7a7468",
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        background: "transparent",
                        border: "none",
                    }}
                >
                    {Icon.x()}
                </button>
            </div>
            {showProgress && (
                <div style={{ height: 2, background: "#f4f1ec" }}>
                    <div
                        className="p3m-toast-progress-fill"
                        style={{
                            height: "100%",
                            background: s.accent,
                            animation: `p3mToastProgress ${duration}s linear forwards`,
                        }}
                    />
                </div>
            )}
            {isLoading && (
                <div
                    style={{
                        height: 2,
                        background: "#f4f1ec",
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            height: "100%",
                            width: "40%",
                            background: `linear-gradient(90deg, transparent, ${s.accent}, transparent)`,
                            animation:
                                "p3mToastSweep 1.4s linear infinite",
                        }}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Function to display a minimal toast notification using the editorial card
 * design. Renders the card inside antd's notification slot so the existing
 * stacking, placement and lifecycle behavior is preserved.
 *
 * @param params - Object containing all notification properties.
 * @returns The notification key (for programmatic destroy).
 */
export const minimalToast = ({
    dispatch,
    _id,
    title,
    message,
    type = NotificationSeverity.INFO,
    description,
    onClick,
    category,
    priority = NotificationPriority.LOW,
    link,
    data,
    expiresAt,
    durationSeconds,
}: MinimalToastParams & { dispatch?: any }): string => {
    ensureStyles();

    const key = `p3m_toast_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 7)}`;
    const isLoading = type === NotificationSeverity.LOADING;

    let duration = 4;
    if (isLoading) {
        duration =
            durationSeconds !== undefined && durationSeconds > 0
                ? durationSeconds
                : 0;
    } else if (durationSeconds !== undefined) {
        duration = durationSeconds;
    } else if (expiresAt) {
        const diffSeconds = Math.floor(
            (expiresAt.getTime() - Date.now()) / 1000
        );
        duration = diffSeconds > 0 ? diffSeconds : 4;
    }

    const close = () => notification.destroy(key);
    const showMarkRead = !!(_id && dispatch);
    const onMarkRead = showMarkRead
        ? () =>
            dispatch(
                updateNotification({
                    _id: _id as string,
                    body: { read: true },
                }) as any
            )
        : undefined;

    notification.open({
        key,
        className: "p3m-toast",
        message: "",
        description: (
            <ToastCard
                severity={type}
                title={title}
                body={description || message}
                link={link}
                showMarkRead={showMarkRead}
                onMarkRead={onMarkRead}
                onClose={close}
                duration={duration}
                isLoading={isLoading}
                priority={priority}
            />
        ),
        duration,
        closable: false,
        placement: "topRight",
        onClick: () => {
            if (onClick) onClick();
            if (data) {
                console.log("Additional Data:", data);
                console.log("Category:", category);
            }
        },
        style: {
            padding: 0,
            background: "transparent",
            boxShadow: "none",
            width: 380,
        },
    });
    return key;
};

const messageToastBase = (
    title: string,
    messageText: string,
    severity: NotificationSeverity,
    durationSeconds?: number
): string =>
    minimalToast({
        title,
        message: messageText,
        type: severity,
        category: NotificationCategory.MESSAGE,
        priority: NotificationPriority.LOW,
        durationSeconds,
    });

// Dedupe: suppress identical severity+message toasts fired within TTL.
// Prevents stacking when parallel requests fail with the same error.
const TOAST_DEDUPE_TTL_MS = 3000;
const recentToasts = new Map<string, { key: string; expiresAt: number }>();

const dedupedToast = (
    title: string,
    messageText: string,
    severity: NotificationSeverity,
    durationSeconds?: number
): string => {
    const now = Date.now();
    const dedupeKey = `${severity}:${messageText}`;
    const existing = recentToasts.get(dedupeKey);
    if (existing && existing.expiresAt > now) {
        return existing.key;
    }
    const key = messageToastBase(title, messageText, severity, durationSeconds);
    recentToasts.set(dedupeKey, { key, expiresAt: now + TOAST_DEDUPE_TTL_MS });
    for (const [k, v] of recentToasts) {
        if (v.expiresAt <= now) recentToasts.delete(k);
    }
    return key;
};

/**
 * App-wide toast helpers (replaces antd `message` API) using the editorial
 * notification card.
 */
export const toast = {
    success: (messageText: string, durationSeconds?: number) =>
        dedupedToast(
            "Success",
            messageText,
            NotificationSeverity.SUCCESS,
            durationSeconds
        ),

    error: (messageText: string, durationSeconds?: number) =>
        dedupedToast(
            "Error",
            messageText,
            NotificationSeverity.ERROR,
            durationSeconds
        ),

    warning: (messageText: string, durationSeconds?: number) =>
        dedupedToast(
            "Warning",
            messageText,
            NotificationSeverity.WARNING,
            durationSeconds
        ),

    info: (messageText: string, durationSeconds?: number) =>
        dedupedToast(
            "Info",
            messageText,
            NotificationSeverity.INFO,
            durationSeconds
        ),

    /**
     * Persistent loading toast when duration is 0 (default). Call `destroy()`
     * when done. Pass a positive `durationSeconds` to auto-dismiss.
     */
    loading: (
        messageText: string,
        durationSeconds: number = 0
    ): { destroy: () => void } => {
        const key = minimalToast({
            title: "Loading",
            message: messageText,
            type: NotificationSeverity.LOADING,
            category: NotificationCategory.MESSAGE,
            priority: NotificationPriority.LOW,
            durationSeconds:
                durationSeconds > 0 ? durationSeconds : undefined,
        });
        return { destroy: () => notification.destroy(key) };
    },

    /** Clears all notification toasts (like antd `message.destroy()`). */
    destroyAll: () => {
        recentToasts.clear();
        notification.destroy();
    },
};
